import type { ModelMessage, ToolApprovalResponse } from "ai";
import type {
	AgentChatApprovalRequestPart,
	AgentChatAssistantPart,
	AgentChatMessage,
} from "./types";

function stableStringify(value: unknown): string {
	if (value === null || typeof value !== "object") {
		return JSON.stringify(value);
	}

	if (Array.isArray(value)) {
		return `[${value.map((entry) => stableStringify(entry)).join(",")}]`;
	}

	const record = value as Record<string, unknown>;
	const keys = Object.keys(record).sort();
	return `{${keys
		.map((key) => `${JSON.stringify(key)}:${stableStringify(record[key])}`)
		.join(",")}}`;
}

/** Same lifecycle tool + same args → one user-facing approval. */
export function getLifecycleApprovalFingerprint(
	toolName: string,
	input: unknown,
) {
	return `${toolName}:${stableStringify(input ?? null)}`;
}

export function getPendingApprovals(
	messages: AgentChatMessage[],
): AgentChatApprovalRequestPart[] {
	const responded = new Set<string>();
	const pending: AgentChatApprovalRequestPart[] = [];

	for (const message of messages) {
		if (message.role !== "assistant") {
			continue;
		}

		for (const part of message.parts) {
			if (part.type === "approval-response") {
				responded.add(part.approvalId);
			}
		}
	}

	for (let index = messages.length - 1; index >= 0; index -= 1) {
		const message = messages[index];
		if (message?.role !== "assistant") {
			continue;
		}

		for (const part of message.parts) {
			if (part.type !== "approval-request") {
				continue;
			}
			if (responded.has(part.approvalId)) {
				continue;
			}
			pending.push(part);
		}

		if (pending.length > 0) {
			break;
		}
	}

	return pending;
}

/** First pending request per tool+input — hides model duplicate parallel calls. */
export function getVisiblePendingApprovals(
	messages: AgentChatMessage[],
): AgentChatApprovalRequestPart[] {
	const seen = new Set<string>();
	const visible: AgentChatApprovalRequestPart[] = [];

	for (const part of getPendingApprovals(messages)) {
		const fingerprint = getLifecycleApprovalFingerprint(
			part.toolName,
			part.input,
		);
		if (seen.has(fingerprint)) {
			continue;
		}
		seen.add(fingerprint);
		visible.push(part);
	}

	return visible;
}

/**
 * Which approval ids to render. Pending retries must surface even when an
 * earlier resolved request shared the same tool+input fingerprint.
 */
export function getRenderableApprovalIds(
	parts: AgentChatAssistantPart[],
): Set<string> {
	const responded = new Set(
		parts
			.filter((part) => part.type === "approval-response")
			.map((part) => part.approvalId),
	);
	const renderable = new Set<string>();
	const seenPendingFingerprints = new Set<string>();
	const seenResolvedFingerprints = new Set<string>();

	for (const part of parts) {
		if (part.type !== "approval-request" || responded.has(part.approvalId)) {
			continue;
		}

		const fingerprint = getLifecycleApprovalFingerprint(
			part.toolName,
			part.input,
		);
		if (seenPendingFingerprints.has(fingerprint)) {
			continue;
		}
		seenPendingFingerprints.add(fingerprint);
		renderable.add(part.approvalId);
	}

	for (const part of parts) {
		if (part.type !== "approval-request" || !responded.has(part.approvalId)) {
			continue;
		}

		const fingerprint = getLifecycleApprovalFingerprint(
			part.toolName,
			part.input,
		);
		if (
			seenPendingFingerprints.has(fingerprint) ||
			seenResolvedFingerprints.has(fingerprint)
		) {
			continue;
		}
		seenResolvedFingerprints.add(fingerprint);
		renderable.add(part.approvalId);
	}

	return renderable;
}

/** @deprecated Prefer getRenderableApprovalIds — pending retries must win. */
export function getPrimaryApprovalIdsInParts(
	parts: AgentChatAssistantPart[],
): Set<string> {
	return getRenderableApprovalIds(parts);
}

export function hasPendingApprovals(messages: AgentChatMessage[]) {
	return getPendingApprovals(messages).length > 0;
}

export function markApprovalResponded(
	messages: AgentChatMessage[],
	approvalId: string,
	approved: boolean,
	reason?: string,
): AgentChatMessage[] {
	const nextMessages = [...messages];

	for (let index = nextMessages.length - 1; index >= 0; index -= 1) {
		const message = nextMessages[index];
		if (message?.role !== "assistant") {
			continue;
		}

		const hasRequest = message.parts.some(
			(part) =>
				part.type === "approval-request" && part.approvalId === approvalId,
		);
		if (!hasRequest) {
			continue;
		}

		const alreadyResponded = message.parts.some(
			(part) =>
				part.type === "approval-response" && part.approvalId === approvalId,
		);
		if (alreadyResponded) {
			return nextMessages;
		}

		nextMessages[index] = {
			...message,
			parts: [
				...message.parts,
				{
					type: "approval-response",
					approvalId,
					approved,
					...(reason ? { reason } : {}),
				},
			],
		};
		return nextMessages;
	}

	return nextMessages;
}

export function markApprovalsResponded(
	messages: AgentChatMessage[],
	approvals: Array<{ approvalId: string; approved: boolean; reason?: string }>,
): AgentChatMessage[] {
	return approvals.reduce(
		(current, approval) =>
			markApprovalResponded(
				current,
				approval.approvalId,
				approval.approved,
				approval.reason,
			),
		messages,
	);
}

/**
 * Apply one Approve/Deny to every pending request with the same tool+input.
 * Models sometimes emit duplicate parallel lifecycle calls.
 */
export function markApprovalRespondedIncludingDuplicates(
	messages: AgentChatMessage[],
	approvalId: string,
	approved: boolean,
	reason?: string,
): AgentChatMessage[] {
	const pending = getPendingApprovals(messages);
	const target = pending.find((part) => part.approvalId === approvalId);
	if (!target) {
		return messages;
	}

	const fingerprint = getLifecycleApprovalFingerprint(
		target.toolName,
		target.input,
	);
	const matching = pending.filter(
		(part) =>
			getLifecycleApprovalFingerprint(part.toolName, part.input) ===
			fingerprint,
	);

	return markApprovalsResponded(
		messages,
		matching.map((part) => {
			const isPrimary = part.approvalId === approvalId;
			return {
				approvalId: part.approvalId,
				// Only the primary should execute when the user approves;
				// identical parallel calls are denied as duplicates.
				approved: isPrimary ? approved : false,
				...(isPrimary
					? reason
						? { reason }
						: {}
					: { reason: "Duplicate parallel tool call" }),
			};
		}),
	);
}

/** Mark denied approval tool-calls completed immediately (before SDK continuation). */
export function completeDeniedApprovalToolCalls(
	messages: AgentChatMessage[],
): AgentChatMessage[] {
	return messages.map((message) => {
		if (message.role !== "assistant") {
			return message;
		}

		const deniedToolCallIds = new Set<string>();
		for (const part of message.parts) {
			if (part.type !== "approval-response" || part.approved) {
				continue;
			}

			const request = message.parts.find(
				(candidate) =>
					candidate.type === "approval-request" &&
					candidate.approvalId === part.approvalId,
			);
			if (request?.type === "approval-request") {
				deniedToolCallIds.add(request.toolCallId);
			}
		}

		if (deniedToolCallIds.size === 0) {
			return message;
		}

		return {
			...message,
			parts: message.parts.map((part) => {
				if (
					part.type === "tool-call" &&
					part.toolCallId &&
					deniedToolCallIds.has(part.toolCallId) &&
					part.state !== "completed"
				) {
					return { ...part, state: "completed" as const };
				}
				return part;
			}),
		};
	});
}

export function buildToolApprovalResponses(
	approvals: Array<{ approvalId: string; approved: boolean; reason?: string }>,
): ToolApprovalResponse[] {
	return approvals.map((approval) => ({
		type: "tool-approval-response",
		approvalId: approval.approvalId,
		approved: approval.approved,
		...(approval.reason ? { reason: approval.reason } : {}),
	}));
}

export function appendToolApprovalMessage(
	continuationMessages: ModelMessage[],
	approvals: ToolApprovalResponse[],
): ModelMessage[] {
	if (approvals.length === 0) {
		return continuationMessages;
	}

	return [
		...continuationMessages,
		{
			role: "tool",
			content: approvals,
		},
	];
}

export function collectApprovalResponses(
	messages: AgentChatMessage[],
): ToolApprovalResponse[] {
	if (hasPendingApprovals(messages)) {
		return [];
	}

	for (let index = messages.length - 1; index >= 0; index -= 1) {
		const message = messages[index];
		if (message?.role !== "assistant") {
			continue;
		}

		const requestIds = new Set(
			message.parts
				.filter((part) => part.type === "approval-request")
				.map((part) => part.approvalId),
		);
		if (requestIds.size === 0) {
			continue;
		}

		const responses = message.parts.filter(
			(
				part,
			): part is Extract<
				AgentChatAssistantPart,
				{ type: "approval-response" }
			> => part.type === "approval-response" && requestIds.has(part.approvalId),
		);

		if (responses.length === 0) {
			return [];
		}

		return buildToolApprovalResponses(responses);
	}

	return [];
}

/** Deny all pending approvals and mark matching tool calls completed. */
export function cancelPendingApprovals(
	messages: AgentChatMessage[],
): AgentChatMessage[] {
	const pending = getPendingApprovals(messages);
	if (pending.length === 0) {
		return messages;
	}

	const pendingByToolCallId = new Map(
		pending.map((part) => [part.toolCallId, part]),
	);
	const pendingIds = new Set(pending.map((part) => part.approvalId));

	return messages.map((message) => {
		if (message.role !== "assistant") {
			return message;
		}

		const ownsPending = message.parts.some(
			(part) =>
				part.type === "approval-request" && pendingIds.has(part.approvalId),
		);
		if (!ownsPending) {
			return message;
		}

		const parts: AgentChatAssistantPart[] = message.parts.map((part) => {
			if (
				part.type === "tool-call" &&
				part.state === "awaiting_approval" &&
				part.toolCallId &&
				pendingByToolCallId.has(part.toolCallId)
			) {
				return { ...part, state: "completed" as const };
			}
			return part;
		});

		for (const approval of pending) {
			if (
				!message.parts.some(
					(part) =>
						part.type === "approval-request" &&
						part.approvalId === approval.approvalId,
				)
			) {
				continue;
			}

			parts.push({
				type: "approval-response",
				approvalId: approval.approvalId,
				approved: false,
				reason: "Cancelled",
			});
		}

		return { ...message, parts };
	});
}
